import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  documentId,
  getDocs,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  runTransaction,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS, LOG_TYPES } from '../config/constants';
import { FirestoreError, AppError } from '../lib/errors';

/**
 * Caracteres del código de invitación.
 * Excluyo I, O, 0, 1 para evitar ambigüedad visual cuando el usuario
 * tipea el código en mayúsculas.
 */
const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const CODE_LENGTH = 6;
const MAX_CODE_GENERATION_ATTEMPTS = 5;

/**
 * Genera un código alfanumérico aleatorio.
 * Usa crypto.getRandomValues para entropía real (no Math.random).
 */
const generateCode = () => {
  const bytes = new Uint8Array(CODE_LENGTH);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => CODE_ALPHABET[b % CODE_ALPHABET.length]).join('');
};

/**
 * Genera un código único verificando contra Firestore.
 * En el peor caso intenta MAX_CODE_GENERATION_ATTEMPTS veces antes de fallar.
 *
 * Probabilidad de colisión con 32^6 = ~1B combinaciones es despreciable
 * para volúmenes esperados, pero la verificación es defensa en profundidad.
 */
const generateUniqueCode = async () => {
  for (let i = 0; i < MAX_CODE_GENERATION_ATTEMPTS; i++) {
    const code = generateCode();
    const inviteRef = doc(db, COLLECTIONS.INVITATIONS, code);
    const snapshot = await getDoc(inviteRef);
    if (!snapshot.exists()) return code;
  }
  throw new AppError('No se pudo generar un código único', 'CODE_GENERATION_FAILED');
};

/**
 * Crea un nuevo hogar con el usuario actual como owner.
 * Retorna { familyId, code }.
 *
 * Flujo:
 *  1. Generar código único.
 *  2. Crear doc del hogar.
 *  3. Crear doc de invitación apuntando al hogar.
 *  4. Actualizar el perfil del usuario con familyId.
 *
 * Idealmente esto sería una transacción única, pero Firestore transactions
 * no soportan create con ID auto-generado de forma cómoda. Lo resolvemos
 * con un batch write tras pre-generar el ID del hogar.
 */
export const createFamily = async ({ uid, name }) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');
  if (!name?.trim()) throw new AppError('El nombre del hogar es requerido', 'INVALID_INPUT');

  const code = await generateUniqueCode();
  const familyRef = doc(collection(db, COLLECTIONS.FAMILIES));
  const familyId = familyRef.id;

  try {
    await runTransaction(db, async (transaction) => {
      const inviteRef = doc(db, COLLECTIONS.INVITATIONS, code);
      const inviteSnap = await transaction.get(inviteRef);
      if (inviteSnap.exists()) {
        throw new AppError('Colisión de código detectada', 'CODE_COLLISION');
      }

      const userRef = doc(db, COLLECTIONS.USERS, uid);
      const userSnap = await transaction.get(userRef);
      if (!userSnap.exists()) {
        throw new AppError('El perfil de usuario no existe', 'USER_NOT_FOUND');
      }
      if (userSnap.data().familyId) {
        throw new AppError('Ya pertenecés a un hogar', 'ALREADY_IN_FAMILY');
      }

      transaction.set(familyRef, {
        name: name.trim(),
        ownerId: uid,
        memberIds: [uid],
        createdAt: serverTimestamp(),
      });

      transaction.set(inviteRef, {
        familyId,
        createdBy: uid,
        createdAt: serverTimestamp(),
      });

      transaction.update(userRef, {
        familyId,
        joinedFamilyAt: serverTimestamp(),
      });
    });

    return { familyId, code };
  } catch (err) {
  if (err instanceof AppError) throw err;
  if (err?.cause instanceof AppError) throw err.cause;
  throw new FirestoreError('No se pudo crear el hogar', err);
}
};

/**
 * Resuelve un código de invitación a un familyId.
 * Lectura única, sin side effects. Útil para previsualizar antes de unirse.
 */
export const resolveInviteCode = async (code) => {
  if (!code?.trim()) throw new AppError('Código requerido', 'INVALID_INPUT');

  const normalized = code.trim().toUpperCase();
  try {
    const inviteRef = doc(db, COLLECTIONS.INVITATIONS, normalized);
    const snapshot = await getDoc(inviteRef);
    if (!snapshot.exists()) {
      throw new AppError('Código de invitación inválido', 'INVALID_CODE');
    }
    return { code: normalized, familyId: snapshot.data().familyId };
  } catch (err) {
    if (err instanceof AppError) throw err;
    throw new FirestoreError('No se pudo verificar el código', err);
  }
};

/**
 * Une al usuario actual a un hogar usando un código de invitación.
 *
 * Transacción atómica: agrega uid a memberIds del hogar Y actualiza
 * el familyId del usuario en una sola operación. Si falla cualquier
 * paso, ambos se revierten — no quedamos con estados inconsistentes.
 */
export const joinFamily = async ({ uid, code }) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');

  const normalized = code?.trim().toUpperCase();
  if (!normalized) throw new AppError('Código requerido', 'INVALID_INPUT');

  try {
    const familyId = await runTransaction(db, async (transaction) => {
      const inviteRef = doc(db, COLLECTIONS.INVITATIONS, normalized);
      const inviteSnap = await transaction.get(inviteRef);
      if (!inviteSnap.exists()) {
        throw new AppError('Código de invitación inválido', 'INVALID_CODE');
      }

      const targetFamilyId = inviteSnap.data().familyId;
      const familyRef = doc(db, COLLECTIONS.FAMILIES, targetFamilyId);
      // No leemos el doc del hogar: las reglas no permiten que un no-miembro
      // lo lea (decisión de privacidad — no exponer el hogar antes de unirse).
      // Si el hogar no existiera (invitación huérfana), el update transaccional
      // de más abajo falla y la transacción se revierte igual.

      const userRef = doc(db, COLLECTIONS.USERS, uid);
      const userSnap = await transaction.get(userRef);
      if (!userSnap.exists()) {
        throw new AppError('Perfil de usuario no encontrado', 'USER_NOT_FOUND');
      }

      const currentFamilyId = userSnap.data().familyId;
      if (currentFamilyId === targetFamilyId) {
        throw new AppError('Ya sos miembro de este hogar', 'ALREADY_MEMBER');
      }
      if (currentFamilyId) {
        throw new AppError('Ya pertenecés a otro hogar', 'ALREADY_IN_FAMILY');
      }

      // Agregar al hogar
      transaction.update(familyRef, {
        memberIds: arrayUnion(uid),
      });

      // Vincular usuario
      transaction.update(userRef, {
        familyId: targetFamilyId,
        joinedFamilyAt: serverTimestamp(),
      });

      return targetFamilyId;
    });

    return { familyId };
  } catch (err) {
    if (err instanceof AppError) throw err;
    if (err?.cause instanceof AppError) throw err.cause;
    throw new FirestoreError('No se pudo unir al hogar', err);
  }
};

/**
 * Permite al usuario abandonar el hogar.
 * Si es el owner y hay otros miembros, debe transferir ownership primero
 * (lo manejaremos en una pantalla dedicada en Fase futura).
 */
export const leaveFamily = async ({ uid, familyId }) => {
  try {
    await runTransaction(db, async (transaction) => {
      const familyRef = doc(db, COLLECTIONS.FAMILIES, familyId);
      const familySnap = await transaction.get(familyRef);
      if (!familySnap.exists()) {
        throw new AppError('El hogar no existe', 'FAMILY_NOT_FOUND');
      }

      const data = familySnap.data();
      if (data.ownerId === uid && data.memberIds.length > 1) {
        throw new AppError(
          'Como propietario, debés transferir el hogar antes de salir',
          'OWNER_CANNOT_LEAVE'
        );
      }

      const userRef = doc(db, COLLECTIONS.USERS, uid);

      // Si es el último miembro, eliminar el hogar
      if (data.memberIds.length === 1) {
        transaction.delete(familyRef);
        // TODO Fase 4: Cloud Function para limpiar pets/logs/invitations
      } else {
        transaction.update(familyRef, {
          memberIds: arrayRemove(uid),
        });
      }

      transaction.update(userRef, { familyId: null });
    });
  } catch (err) {
    if (err instanceof AppError) throw err;
    if (err?.cause instanceof AppError) throw err.cause;
    throw new FirestoreError('No se pudo salir del hogar', err);
  }
};

/**
 * Suscripción reactiva al hogar. Retorna unsubscribe.
 * Si el hogar deja de existir o el usuario fue removido, dispara onChange(null).
 */
export const subscribeToFamily = (familyId, onChange, onError) => {
  if (!familyId) {
    queueMicrotask(() => onChange(null));
    return () => {};
  }

  const familyRef = doc(db, COLLECTIONS.FAMILIES, familyId);
  return onSnapshot(
    familyRef,
    (snapshot) => {
      if (!snapshot.exists()) {
        onChange(null);
        return;
      }
      onChange({ id: snapshot.id, ...snapshot.data() });
    },
    (err) => {
      console.error('[familyService] Subscription error:', err);
      onError?.(new FirestoreError('Error en la suscripción al hogar', err));
    }
  );
};

/**
 * Carga los perfiles de todos los miembros del hogar.
 * Usa where(documentId, 'in', uids) — limitado a 30 IDs por query (Firestore).
 * Para hogares más grandes habría que paginar, pero 30 es más que suficiente
 * para el caso de uso real (familia ≤ 10 personas).
 */
export const fetchFamilyMembers = async (memberIds) => {
  if (!memberIds || memberIds.length === 0) return [];

  // Firestore 'in' operator: máximo 30 elementos por query
  const FIRESTORE_IN_LIMIT = 30;
  if (memberIds.length > FIRESTORE_IN_LIMIT) {
    throw new AppError(
      `El hogar excede el límite de ${FIRESTORE_IN_LIMIT} miembros`,
      'TOO_MANY_MEMBERS'
    );
  }

  try {
    const usersRef = collection(db, COLLECTIONS.USERS);
    const q = query(usersRef, where(documentId(), 'in', memberIds));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    throw new FirestoreError('No se pudieron cargar los miembros', err);
  }
};

/**
 * Carga el código activo de invitación para un hogar.
 * Búsqueda inversa: el hogar no guarda el código, lo guarda invitations.
 */
export const fetchInviteCode = async (familyId) => {
  try {
    const invitesRef = collection(db, COLLECTIONS.INVITATIONS);
    const q = query(invitesRef, where('familyId', '==', familyId));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    // Asumimos un código activo por hogar; el primero gana
    return snapshot.docs[0].id;
  } catch (err) {
    throw new FirestoreError('No se pudo obtener el código de invitación', err);
  }
};

/**
 * Rota el código de invitación: elimina el actual y genera uno nuevo.
 * Útil cuando el código se compartió de más o se quiere invalidar.
 */
export const rotateInviteCode = async ({ uid, familyId }) => {
  try {
    const newCode = await generateUniqueCode();
    const oldCode = await fetchInviteCode(familyId);

    await runTransaction(db, async (transaction) => {
      // Verificar permisos: solo owner puede rotar
      const familyRef = doc(db, COLLECTIONS.FAMILIES, familyId);
      const familySnap = await transaction.get(familyRef);
      if (!familySnap.exists() || familySnap.data().ownerId !== uid) {
        throw new AppError('No tenés permisos para rotar el código', 'FORBIDDEN');
      }

      if (oldCode) {
        transaction.delete(doc(db, COLLECTIONS.INVITATIONS, oldCode));
      }
      transaction.set(doc(db, COLLECTIONS.INVITATIONS, newCode), {
        familyId,
        createdBy: uid,
        createdAt: serverTimestamp(),
      });
    });

    return newCode;
  } catch (err) {
    if (err instanceof AppError) throw err;
    if (err?.cause instanceof AppError) throw err.cause;
    throw new FirestoreError('No se pudo rotar el código', err);
  }
};

/**
 * Expulsa a un miembro del hogar (kick). Solo el owner puede ejecutarlo.
 *
 * Convenciones de seguridad y limitaciones conocidas:
 *
 *  - El owner NO puede expulsarse a sí mismo: para irse, primero debe
 *    transferir ownership (ver `transferOwnership`) y después usar
 *    `leaveFamily`. Esto previene dejar el hogar sin owner.
 *
 *  - El service SOLO toca `families/{familyId}.memberIds` (arrayRemove
 *    del expulsado). NO toca `users/{memberUid}.familyId`. Razón: las
 *    Firestore Rules sólo permiten que un usuario actualice su propio
 *    doc (`isSelf(userId)`). El owner no tiene permiso para escribir
 *    en el doc de otro user, ni siquiera para limpiar su `familyId`.
 *
 *    Consecuencia: el `users/{memberUid}.familyId` queda stale (apunta
 *    a un hogar donde ya no es miembro). El cliente del expulsado
 *    detecta el mismatch cuando intenta leer el family (la rule de
 *    read falla con permission-denied) y limpia su `familyId` solo
 *    (self-update legal). Ver FamilyContext.handleAccessLost.
 *
 *  - Limitación de displayName en bitácora histórica: los logs son
 *    inmutables y siguen apuntando al `userId` del expulsado. Pero la
 *    rule `users.read` permite leer solo `isSelf || mismo hogar`. Después
 *    de expulsado, el resto del hogar pierde acceso a su user doc → el
 *    LogTimeline va a mostrar "Alguien" en lugar del nombre original.
 *    Decisión aceptada para MVP. Solución dura futura: snapshot del
 *    displayName en `log.payload.userDisplayName` al crear cada log.
 *    Documentado como deuda en SECURITY.md.
 *
 *  - Trazabilidad: se crea un log con type='note' y reason='member_kicked'
 *    + payload con `kickedUid` para audit. Inmutable por rules.
 */
export const kickMember = async ({ uid, familyId, memberUid }) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');
  if (!familyId) throw new AppError('Hogar requerido', 'INVALID_INPUT');
  if (!memberUid) throw new AppError('Miembro a expulsar requerido', 'INVALID_INPUT');
  if (uid === memberUid) {
    throw new AppError(
      'Para irte del hogar usá "Abandonar este hogar"',
      'CANNOT_KICK_SELF'
    );
  }

  try {
    await runTransaction(db, async (transaction) => {
      const familyRef = doc(db, COLLECTIONS.FAMILIES, familyId);
      const familySnap = await transaction.get(familyRef);
      if (!familySnap.exists()) {
        throw new AppError('El hogar no existe', 'FAMILY_NOT_FOUND');
      }
      const data = familySnap.data();

      if (data.ownerId !== uid) {
        throw new AppError(
          'Solo el owner del hogar puede expulsar miembros',
          'FORBIDDEN'
        );
      }
      if (data.ownerId === memberUid) {
        // Doble guard: aunque uid !== memberUid, igual no permitimos kick
        // al owner. Esto cubriría un caso edge donde uid sea otro pero
        // memberUid sea el owner — no debería pasar dado que solo el owner
        // puede llegar acá, pero defensa en profundidad.
        throw new AppError(
          'No se puede expulsar al owner. Transferí ownership primero.',
          'CANNOT_KICK_OWNER'
        );
      }
      if (!data.memberIds.includes(memberUid)) {
        throw new AppError(
          'Esa persona no es miembro del hogar',
          'NOT_A_MEMBER'
        );
      }

      // Update family.memberIds (sin el expulsado).
      transaction.update(familyRef, {
        memberIds: arrayRemove(memberUid),
      });

      // Log inmutable de la acción.
      const logRef = doc(collection(db, COLLECTIONS.LOGS));
      transaction.set(logRef, {
        familyId,
        feedingGroupId: null,
        petId: null,
        userId: uid,
        type: LOG_TYPES.NOTE,
        timestamp: serverTimestamp(),
        payload: {
          reason: 'member_kicked',
          kickedUid: memberUid,
        },
      });
    });
  } catch (err) {
    if (err instanceof AppError) throw err;
    if (err?.cause instanceof AppError) throw err.cause;
    throw new FirestoreError('No se pudo expulsar al miembro', err);
  }
};

/**
 * Transfiere la propiedad del hogar al miembro indicado. Solo el owner
 * actual puede ejecutarlo. El nuevo owner debe ser ya miembro.
 *
 * Es una operación reversible (el nuevo owner puede transferir de vuelta).
 * Solo cambia `families/{familyId}.ownerId`; NO toca `memberIds`.
 *
 * Las Firestore Rules ya validan el invariante `ownerId in memberIds`
 * (familyOwnerIsMember). Acá lo chequeamos client-side antes del write
 * para dar mejor error que un permission-denied genérico.
 */
export const transferOwnership = async ({ uid, familyId, newOwnerUid }) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');
  if (!familyId) throw new AppError('Hogar requerido', 'INVALID_INPUT');
  if (!newOwnerUid) throw new AppError('Nuevo owner requerido', 'INVALID_INPUT');
  if (uid === newOwnerUid) {
    throw new AppError('Ya sos owner del hogar', 'ALREADY_OWNER');
  }

  try {
    await runTransaction(db, async (transaction) => {
      const familyRef = doc(db, COLLECTIONS.FAMILIES, familyId);
      const familySnap = await transaction.get(familyRef);
      if (!familySnap.exists()) {
        throw new AppError('El hogar no existe', 'FAMILY_NOT_FOUND');
      }
      const data = familySnap.data();

      if (data.ownerId !== uid) {
        throw new AppError(
          'Solo el owner actual puede transferir ownership',
          'FORBIDDEN'
        );
      }
      if (!data.memberIds.includes(newOwnerUid)) {
        throw new AppError(
          'El nuevo owner debe ser miembro del hogar primero',
          'NOT_A_MEMBER'
        );
      }

      transaction.update(familyRef, {
        ownerId: newOwnerUid,
      });

      const logRef = doc(collection(db, COLLECTIONS.LOGS));
      transaction.set(logRef, {
        familyId,
        feedingGroupId: null,
        petId: null,
        userId: uid,
        type: LOG_TYPES.NOTE,
        timestamp: serverTimestamp(),
        payload: {
          reason: 'ownership_transferred',
          previousOwnerUid: uid,
          newOwnerUid,
        },
      });
    });
  } catch (err) {
    if (err instanceof AppError) throw err;
    if (err?.cause instanceof AppError) throw err.cause;
    throw new FirestoreError('No se pudo transferir el hogar', err);
  }
};